"use client";

import { ArrowUpDown, Globe, Link2, MoreHorizontal, RefreshCw, Unlink } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { format } from 'date-fns';

// Define the shape of the data for an activation code
// This should align with the data fetched from the `listUserCodes` tRPC procedure
export type UserActivationCode = {
  id: string;
  code: string;
  status: 'pending' | 'active' | 'used' | 'expired' | 'revoked' | 'assigned';
  createdAt: Date | string;
  updatedAt: Date | string;
  productId: string;
  userId: string;
  orderItemId: string | null;
  expiresAt: Date | string | null;
  product: {
    id: string;
    name: string;
    description: string | null;
  };
  activationCodeBindings: {
    id: string;
    bindingType: 'domain' | string;
    bindingValue: string;
    createdAt: Date | string;
  }[];
};

// Type for action handlers that will be passed to the columns
export type ActivationCodeActionHandlers = {
  onResetCode: (codeId: string) => void;
  onBindDomain: (codeId: string) => void;
  onUnbindDomain: (codeId: string, bindingId: string) => void;
};

export const getActivationCodeColumns = (actions: ActivationCodeActionHandlers, t: TFunction): ColumnDef<UserActivationCode>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected()
            ? true
            : table.getIsSomePageRowsSelected()
            ? "indeterminate"
            : false
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t('dataTable.selectAllLabel')}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={t('dataTable.selectRowLabel')}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "code",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t("userActivationCodes.columns.code", "Code")}
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => <div className="font-mono">{row.getValue("code")}</div>,
  },
  {
    accessorKey: "product.name",
    header: t("userActivationCodes.columns.product", "Product"),
    cell: ({ row }) => row.original.product.name,
  },
  {
    accessorKey: "status",
    header: t("userActivationCodes.columns.status", "Status"),
    cell: ({ row }) => {
      const status = row.getValue("status") as UserActivationCode["status"];
      let variant: "default" | "secondary" | "destructive" | "outline" = "secondary";
      if (status === "active" || status === "assigned") variant = "default";
      if (status === "expired" || status === "revoked" || status === "used") variant = "destructive";
      return <Badge variant={variant} className="capitalize">{t(`userActivationCodeStatus.${status}`, status)}</Badge>;
    },
  },
  {
    accessorKey: "activationCodeBindings",
    header: t("userActivationCodes.columns.boundDomains", "Bound Domains"),
    cell: ({ row }) => {
      const bindings = row.original.activationCodeBindings.filter(b => b.bindingType === 'domain');
      if (bindings.length === 0) {
        return <span className="text-muted-foreground">{t("common.none", "None")}</span>;
      }
      return (
        <div className="flex flex-col space-y-1">
          {bindings.map(binding => (
            <div key={binding.id} className="flex items-center text-sm">
              <Globe className="mr-2 h-4 w-4 text-muted-foreground" />
              {binding.bindingValue}
            </div>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: "expiresAt",
    header: t("userActivationCodes.columns.expiresAt", "Expires At"),
    cell: ({ row }) => {
      const expiresAt = row.getValue("expiresAt") as string | null;
      return expiresAt ? format(new Date(expiresAt), "PPpp") : <span className="text-muted-foreground">{t("common.notApplicable", "N/A")}</span>;
    },
  },
  {
    id: "actions",
    header: () => <div className="text-right">{t("userActivationCodes.columns.actions", "Actions")}</div>,
    cell: ({ row }) => {
      const code = row.original;
      const canReset = code.status === 'active' || code.status === 'assigned';
      const domainBindings = code.activationCodeBindings.filter(b => b.bindingType === 'domain');
      const canBind = (code.status === 'active' || code.status === 'assigned') && domainBindings.length === 0;

      return (
        <div className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">{t("common.openMenu", "Open menu")}</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("userActivationCodes.actions.title", "Actions")}</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => actions.onResetCode(code.id)}
                disabled={!canReset}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {t("userActivationCodes.actions.reset", "Reset Code")}
              </DropdownMenuItem>
              <TooltipProvider>
                <Tooltip open={!canBind ? undefined : false}>
                  <TooltipTrigger asChild>
                    {/* The div is necessary for TooltipTrigger to correctly work with a disabled DropdownMenuItem */}
                    <div className={!canBind ? "cursor-not-allowed" : ""}>
                      <DropdownMenuItem
                        onClick={() => actions.onBindDomain(code.id)}
                        disabled={!canBind}
                        // Prevent click events from bubbling up when disabled and tooltip is shown
                        onSelect={!canBind ? (e) => e.preventDefault() : undefined}
                      >
                        <Link2 className="mr-2 h-4 w-4" />
                        {t("userActivationCodes.actions.bindDomain", "Bind Domain")}
                      </DropdownMenuItem>
                    </div>
                  </TooltipTrigger>
                  {!canBind && (
                    <TooltipContent>
                      <p>{t("userActivationCodes.actions.domainAlreadyBoundTooltip", "A domain is already bound to this code.")}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
              <DropdownMenuSeparator />
              {domainBindings.length > 0 && (
                <>
                  <DropdownMenuLabel>{t("userActivationCodes.actions.unbindDomainTitle", "Unbind Domain")}</DropdownMenuLabel>
                  {domainBindings.map(binding => (
                    <DropdownMenuItem
                      key={binding.id}
                      onClick={() => actions.onUnbindDomain(code.id, binding.id)}
                      disabled={!(code.status === 'active' || code.status === 'assigned')}
                    >
                      <Unlink className="mr-2 h-4 w-4" />
                      {binding.bindingValue}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
               {domainBindings.length === 0 && (
                  <DropdownMenuItem disabled>
                      <span className="text-xs text-muted-foreground">{t("userActivationCodes.actions.noDomainsToUnbind", "No domains to unbind")}</span>
                  </DropdownMenuItem>
               )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  },
];